import React, { useState, useEffect } from 'react';
import { Network, Plus, Trash2, Cpu, CircleCheck, Loader2, Edit } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import axios from 'axios';

const generateUUID = () => {
    if (typeof crypto !== 'undefined' && crypto.randomUUID) {
        return crypto.randomUUID();
    }
    return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function(c) {
        const r = Math.random() * 16 | 0, v = c === 'x' ? r : (r & 0x3 | 0x8);
        return v.toString(16);
    });
};

export default function WorkflowMapper() {
  const { token, user } = useAuth();
  
  const [interventionOptions, setInterventionOptions] = useState(['Pending Assessment']);

  const [workflows, setWorkflows] = useState([
    { 
      id: generateUUID(),
      process_name: '', 
      current_tools_used: '', 
      hours_spent_per_week: '', 
      fte_cost: '', 
      identified_bottleneck: '',
      ai_suitability: 'Pending Assessment',
      sync_status: 'NEW' 
    }
  ]);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const baseURL = process.env.REACT_APP_API_URL || 'http://localhost:5000';
        
        // Fetch AI Matrix
        const matrixRes = await axios.get(`${baseURL}/api/sprint/intervention-matrix/${user.org_id}`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        
        if (matrixRes.data && matrixRes.data.length > 0) {
          const strategies = matrixRes.data.map(item => item.strategy_name);
          setInterventionOptions(['Pending Assessment', ...strategies]);
        } else {
          setInterventionOptions([
            'Pending Assessment', 'High - LLM / RAG', 'High - Agentic Workflow', 
            'Medium - Standard Automation', 'Low - Keep Human in Loop'
          ]);
        }

        // Fetch Existing Workflows
        const wfRes = await axios.get(`${baseURL}/api/sprint/workflow-map/${user.org_id}`, {
          headers: { Authorization: `Bearer ${token}` }
        });

        if (wfRes.data && wfRes.data.length > 0) {
          const loadedWorkflows = wfRes.data.map(wf => ({
            id: wf.id,
            process_name: wf.process_name || '',
            current_tools_used: wf.current_tools_used || '',
            hours_spent_per_week: wf.hours_spent_per_week || '',
            fte_cost: wf.fte_cost || '',
            identified_bottleneck: wf.identified_bottleneck || '',
            ai_suitability: wf.ai_suitability || 'Pending Assessment',
            sync_status: 'SENT' 
          }));
          setWorkflows(loadedWorkflows);
        }
      } catch (error) {
        console.error("Failed to load data", error);
      }
    };

    if (user?.org_id) fetchData();
  }, [token, user?.org_id]);

  const addWorkflow = () => {
    setWorkflows([...workflows, { 
      id: generateUUID(),
      process_name: '', 
      current_tools_used: '', 
      hours_spent_per_week: '', 
      fte_cost: '', 
      identified_bottleneck: '',
      ai_suitability: 'Pending Assessment',
      sync_status: 'NEW' 
    }]);
  };

  // UPDATED: Now securely deletes from the database first!
  const removeWorkflow = async (index) => {
    const wf = workflows[index];
    
    // If the record was already saved to the database, delete it there first
    if (wf.sync_status === 'SENT' || wf.sync_status === 'MODIFIED') {
        try {
            const baseURL = process.env.REACT_APP_API_URL || 'http://localhost:5000';
            await axios.delete(`${baseURL}/api/sprint/workflow-map/${wf.id}`, {
                headers: { Authorization: `Bearer ${token}` }
            });
        } catch (error) {
            console.error("Failed to delete workflow from database:", error);
            alert('Failed to delete the workflow from the server.');
            return; // Stop the UI deletion if the database deletion fails
        }
    }

    // Remove from UI
    const newWorkflows = workflows.filter((_, i) => i !== index);
    setWorkflows(newWorkflows);
  };

  const handleInputChange = (index, field, value) => {
    const newWorkflows = [...workflows];
    newWorkflows[index][field] = value;
    setWorkflows(newWorkflows);
  };

  // NEW: Explicit Edit Button functionality
  const handleEditClick = (index) => {
    const newWorkflows = [...workflows];
    newWorkflows[index].sync_status = 'MODIFIED'; // Unlocks the row
    setWorkflows(newWorkflows);
  };

  const handleSendSingle = async (index) => {
    const wf = workflows[index];

    if (!wf.process_name) {
      alert("Please enter a Process Name before sending.");
      return;
    }

    const savingWf = [...workflows];
    savingWf[index].sync_status = 'SAVING';
    setWorkflows(savingWf);

    try {
      const baseURL = process.env.REACT_APP_API_URL || 'http://localhost:5000';
      const response = await axios.post(`${baseURL}/api/sprint/workflow-map`, 
        { orgId: user.org_id, workflows: [wf] }, 
        { headers: { Authorization: `Bearer ${token}` } }
      );

      if (response.data.success) {
        const successWf = [...workflows];
        successWf[index].sync_status = 'SENT';
        setWorkflows(successWf);
      }
    } catch (error) {
      console.error("Error saving workflow:", error);
      alert('Failed to send to synthesizer.');
      const errorWf = [...workflows];
      errorWf[index].sync_status = 'MODIFIED';
      setWorkflows(errorWf);
    }
  };

  return (
    <div className="max-w-7xl mx-auto p-6">
      <div className="flex justify-between items-center mb-8">
        <div>
          <h1 className="text-3xl font-bold text-slate-900">Day 2: Workflow Mapping</h1>
          <p className="text-slate-500 mt-2">Surface process friction and map individual AI entry points.</p>
        </div>
        <button onClick={addWorkflow} className="flex items-center gap-2 bg-slate-900 text-white px-4 py-2.5 rounded-lg hover:bg-slate-800 transition-colors shadow-sm font-bold text-sm">
          <Plus className="w-4 h-4" /> Add Process
        </button>
      </div>

      <div className="bg-white rounded-xl shadow-sm border border-slate-200 overflow-hidden">
        <table className="w-full text-left border-collapse">
          <thead>
            <tr className="bg-slate-50 border-b border-slate-200 text-sm text-slate-600">
              <th className="p-4 font-semibold">Process Name</th>
              <th className="p-4 font-semibold">Current Tools</th>
              <th className="p-4 font-semibold">Hrs / Week</th>
              <th className="p-4 font-semibold">Annual FTE Cost ($)</th>
              <th className="p-4 font-semibold">Identified Bottleneck</th>
              <th className="p-4 font-semibold w-48">Intervention Strategy</th>
              <th className="p-4 font-semibold text-center w-32">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {workflows.map((wf, index) => {
              const isSent = wf.sync_status === 'SENT';
              const isSaving = wf.sync_status === 'SAVING';

              return (
                <tr key={wf.id} className="hover:bg-slate-50/50 transition-colors">
                  <td className="p-3">
                    <input type="text" disabled={isSent} className={`w-full border-slate-300 rounded focus:ring-blue-500 focus:border-blue-500 text-sm ${isSent ? 'bg-slate-50 text-slate-500 cursor-not-allowed border-transparent' : ''}`} placeholder="Invoice Processing" value={wf.process_name} onChange={(e) => handleInputChange(index, 'process_name', e.target.value)} />
                  </td>
                  <td className="p-3">
                    <input type="text" disabled={isSent} className={`w-full border-slate-300 rounded focus:ring-blue-500 focus:border-blue-500 text-sm ${isSent ? 'bg-slate-50 text-slate-500 cursor-not-allowed border-transparent' : ''}`} placeholder="SAP, Excel" value={wf.current_tools_used} onChange={(e) => handleInputChange(index, 'current_tools_used', e.target.value)} />
                  </td>
                  <td className="p-3">
                    <input type="number" disabled={isSent} className={`w-full border-slate-300 rounded focus:ring-blue-500 focus:border-blue-500 text-sm ${isSent ? 'bg-slate-50 text-slate-500 cursor-not-allowed border-transparent' : ''}`} placeholder="40" value={wf.hours_spent_per_week} onChange={(e) => handleInputChange(index, 'hours_spent_per_week', e.target.value)} />
                  </td>
                  <td className="p-3">
                    <input type="number" disabled={isSent} className={`w-full border-slate-300 rounded focus:ring-blue-500 focus:border-blue-500 text-sm ${isSent ? 'bg-slate-50 text-slate-500 cursor-not-allowed border-transparent' : ''}`} placeholder="65000" value={wf.fte_cost} onChange={(e) => handleInputChange(index, 'fte_cost', e.target.value)} />
                  </td>
                  <td className="p-3">
                    <textarea rows="1" disabled={isSent} className={`w-full border-slate-300 rounded focus:ring-blue-500 focus:border-blue-500 text-sm resize-none ${isSent ? 'bg-slate-50 text-slate-500 cursor-not-allowed border-transparent' : ''}`} placeholder="Data entry errors..." value={wf.identified_bottleneck} onChange={(e) => handleInputChange(index, 'identified_bottleneck', e.target.value)} />
                  </td>
                  <td className="p-3">
                    <select 
                      disabled={isSent}
                      className={`w-full border-slate-300 rounded focus:ring-blue-500 focus:border-blue-500 text-sm ${isSent ? 'bg-slate-50 text-slate-500 cursor-not-allowed border-transparent' : 'bg-white'}`}
                      value={wf.ai_suitability} 
                      onChange={(e) => handleInputChange(index, 'ai_suitability', e.target.value)}
                    >
                      {interventionOptions.map((option, i) => (
                        <option key={i} value={option}>{option}</option>
                      ))}
                    </select>
                  </td>
                  
                  {/* EXPLICIT ACTION BUTTONS */}
                  <td className="p-3 text-center">
                    <div className="flex items-center justify-center gap-2">
                      
                      {/* 1. Loader (When Saving) */}
                      {isSaving && (
                        <button disabled className="p-2 rounded-md bg-blue-100 text-blue-600">
                          <Loader2 className="w-4 h-4 animate-spin" />
                        </button>
                      )}

                      {/* 2. Edit Button (When successfully saved/locked) */}
                      {isSent && !isSaving && (
                        <button 
                          onClick={() => handleEditClick(index)} 
                          title="Edit Process"
                          className="p-2 rounded-md bg-slate-100 text-slate-600 hover:bg-slate-200 hover:text-slate-900 transition-colors"
                        >
                          <Edit className="w-4 h-4" />
                        </button>
                      )}

                      {/* 3. Send / Resend Button (When unlocked/editing) */}
                      {!isSent && !isSaving && (
                        <button 
                          onClick={() => handleSendSingle(index)}
                          title={wf.sync_status === 'MODIFIED' ? "Resend Update to Synthesizer" : "Send to Synthesizer"}
                          className={`p-2 rounded-md transition-all ${
                            wf.sync_status === 'MODIFIED' ? 'bg-amber-100 text-amber-700 hover:bg-amber-200' : 'bg-blue-100 text-blue-600 hover:bg-blue-200'
                          }`}
                        >
                          <Cpu className="w-4 h-4" />
                        </button>
                      )}

                      {/* 4. Trash Button (Always present) */}
                      <button onClick={() => removeWorkflow(index)} className="p-2 text-slate-400 hover:text-red-600 hover:bg-red-50 rounded-md transition-colors">
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </td>

                </tr>
              );
            })}
          </tbody>
        </table>
        
        {workflows.length === 0 && (
          <div className="p-12 text-center text-slate-500 flex flex-col items-center bg-slate-50">
            <Network className="w-12 h-12 text-slate-300 mb-3" />
            <p className="text-lg font-medium text-slate-700">No workflows mapped yet.</p>
            <p className="text-sm mt-1">Start interviewing department heads and click "Add Process" above.</p>
          </div>
        )}
      </div>
    </div>
  );
}